import AssignMenu from 'components/assign/AssignMenu';
import Badge from 'components/badge/badge';
import CellButton from 'components/table/components/CellButton';
import EmptyCell from 'components/table/components/EmptyCell';
import TableCell from 'components/table/components/TableCell';
import TableRow from 'components/table/components/TableRow';
import useText from 'hooks/useText';
import CompanyAction from './CompanyAction';
import CompanyIdentity from './CompanyIdentity';
import { commercialsOf, recruitersOf } from './companyAssignees';

/**
 * A company in the table, a row of 72 px on the admin's TableRow and
 * TableCell (compact): it comes in with the rows' cascade, stays lit once
 * opened (`selectedId`) and lights up when its commercial or its recruiter
 * changes. The cells after its category follow its tab: the lost companies show
 * why and when to try again.
 */
const CompanyTableRow = ({ company, tab, index, selectedId, team, meId, onOpen, onOpenGroup, onAssign }) => {
  const { text } = useText('companies');
  const assignMenu = (role) => (
    <span className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <AssignMenu
        value={company[role === 'recruiter' ? 'recruiterId' : 'commercialId']}
        employees={team}
        options={role === 'recruiter' ? recruitersOf(team) : commercialsOf(team)}
        meId={meId}
        emptyLabel={text(role === 'recruiter' ? 'assignRecruiter' : 'assignCommercial')}
        onChange={(personId) => onAssign(company, role, personId)}
      />
    </span>
  );
  return (
    <TableRow
      itemId={company._id}
      selectedId={selectedId}
      onClick={onOpen}
      className="h-[72px] motion-safe:animate-fadeUp"
      // Capped so the bottom of a long page does not wait for the top.
      style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
      flashSignature={`${company.commercialId}|${company.recruiterId}`}
    >
      <TableCell compact position="first">
        <CompanyIdentity name={company.name} legalName={company.reason_social} />
      </TableCell>
      <TableCell compact>{company.department ? <span className="text-gray-700">{company.department}</span> : <EmptyCell />}</TableCell>
      <TableCell compact>{company.category ? <Badge type={company.category.badge}>{company.category.label}</Badge> : <EmptyCell />}</TableCell>
      {tab === 'lost' ? (
        <>
          <TableCell compact>
            <span className="text-gray-700">{company.lostReason}</span>
          </TableCell>
          <TableCell compact>
            {company.relaunchOn ? (
              <CompanyAction>{company.relaunch}</CompanyAction>
            ) : (
              <span className="whitespace-nowrap text-gray-400">{company.relaunch}</span>
            )}
          </TableCell>
          <TableCell compact>
            <span className="whitespace-nowrap text-gray-700">{company.lostOn}</span>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell compact>
            {company.groupId ? (
              <span className="block max-w-[150px]">
                <CellButton tooltip={text('openGroup')} onClick={() => onOpenGroup(company.groupId)}>
                  {company.groups}
                </CellButton>
              </span>
            ) : company.groups ? (
              <span className="block max-w-[160px] truncate text-gray-700">{company.groups}</span>
            ) : (
              <EmptyCell />
            )}
          </TableCell>
          <TableCell compact>{company.action && <CompanyAction>{company.action}</CompanyAction>}</TableCell>
          {/* « Créé le » for a prospect; a client's date stays off the table. */}
          {tab === 'prospects' && (
            <TableCell compact>
              <span className="whitespace-nowrap text-gray-700">{company.since}</span>
            </TableCell>
          )}
          {tab === 'old' && <TableCell compact>{assignMenu('recruiter')}</TableCell>}
        </>
      )}
      <TableCell compact>{assignMenu('commercial')}</TableCell>
      <TableCell compact position="last" />
    </TableRow>
  );
};

export default CompanyTableRow;
